import React, { ReactElement, useState } from 'react';
import { NavigationButtons } from '../../shared/NavigationButtons';
import { UserInformationForm } from './UserInformationForm';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  CoverageCheckupState,
  goToNextStep,
  updateCoverageCheckupState,
} from '../../../store/step';
import { userInformationSchema } from '../../schema/userInformationSchema';
import { CardPadding } from '../../shared/CardStyles';
import { CardHeader } from '../../shared/CardStyles/CardHeader';
import { postCoverageCheckupResults } from '../../../utils/api/postCoverageCheckupResults';
import { RecaptchaCompleteParam, useRecaptcha } from '@frontman/utils';
import { useFormInteractionTracking } from '../../../hooks/useFormInteractionTracking';
import Icon from '@ramsey-design-system/icon';
import { ErrorOutlinedIcon } from '@ramsey-design-system/icons';
import { createFormTracker } from '@rs/form-tracking-sdk';

function UserInformationStep(): ReactElement {
  const [submissionError, setSubmissionError] = useState(false);
  const dispatch = useDispatch();
  const {
    firstName,
    lastName,
    email,
    completeFpuActionItemFunction,
    actionItem,
  } = useSelector((state: RootState) => state.steps);

  const formRef = useFormInteractionTracking('user_information');

  const data = useSelector((state: RootState) => state.steps);
  const recaptchaKey = '6LfAcX8UAAAAADOKyfvhIPNNt2bpHWA1BP0mH10m';

  const { RecaptchaComponent, startRecaptchaChallenge } = useRecaptcha<
    Partial<CoverageCheckupState>
  >({
    recaptchaKey,
    onRecaptchaComplete,
  });

  async function onRecaptchaComplete(
    recaptchaParams: RecaptchaCompleteParam
  ): Promise<void> {
    const payload = { ...data };
    const formTracker = createFormTracker({
      formId: 'coverage_checkup_submission',
    });
    try {
      const guid = await postCoverageCheckupResults(payload, recaptchaParams);
      completeFpuActionItemFunction(actionItem);
      formTracker.trackValidSubmission();
      formTracker.trackModule({
        type: 'registration',
        category: 'email',
        label: 'pm_coverage_checkup',
      });
      dispatch(updateCoverageCheckupState({ guid }));
    } catch (e) {
      console.error(e);
      setSubmissionError(true);
      formTracker.trackInvalidSubmission();
      return;
    }

    dispatch(goToNextStep());
  }

  async function onSubmit(
    values: Partial<CoverageCheckupState>
  ): Promise<void> {
    dispatch(updateCoverageCheckupState(values));
    startRecaptchaChallenge();
  }

  return (
    <CardPadding>
      <CardHeader
        header="Great! Let's get your results."
        subHeader=" We'll use this info to send your customized Coverage Checkup results to
          you."
      />
      <Formik
        onSubmit={onSubmit}
        initialValues={{ firstName, lastName, email }}
        validationSchema={userInformationSchema}
      >
        <Form className="rds-Input" ref={formRef}>
          <UserInformationForm />
          <>
            {submissionError && (
              <div className="flex p-4 bg-red-5 box-border border-solid border border-red-50 rounded-md mt-2 text-red-90 flex-col md:flex-row">
                <Icon
                  icon={ErrorOutlinedIcon}
                  className="self-center text-red-50 pb-2 md:mr-2"
                  size="xlarge"
                />
                <div>
                  Whoops! There was a problem on our end. Try submitting again
                  or try again later.
                </div>
              </div>
            )}
          </>
          <NavigationButtons />
          <div className="text-2 mt-2 font-normal text-gray-60">
            <p>
              By clicking the “Submit Application” button, you agree to Ramsey
              Solutions’{' '}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://policies.ramseysolutions.net/terms-of-use"
                className="underline"
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://policies.ramseysolutions.net/privacy-policy"
              >
                Privacy Policy
              </a>
              {'. '}
              This site is protected by reCAPTCHA and the Google{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </div>
        </Form>
      </Formik>
      {RecaptchaComponent}
    </CardPadding>
  );
}

export { UserInformationStep };
